import React from "react";
import { unstable_batchedUpdates as batch } from "react-dom";
import styled from "styled-components";
import { Box } from "../Box";
import { useIsFocused } from "../hooks/useIsFocused";
import { Icon } from "../Icon";
import { PropError, PropLabel, PropOption } from "../PropX";
import { style } from "../style";
import { FatButton } from "../__Internal__";
import { HeadlessSelectMenu } from "./HeadlessSelectMenu";

const ICON_SIZE = "24px";

type GridTemplateProps = {
  $hasSubtitle: boolean;
  $hasIcon: boolean;
};

const calculateGridTemplate = (props: GridTemplateProps) => {
  const gridTemplateColumns: string[] = [];
  const gridTemplateAreas = {
    row1: [] as string[],
    row2: [] as string[],
    row3: [] as string[],
    row4: [] as string[],
  };

  if (props.$hasIcon) {
    gridTemplateAreas.row1.push("icon");
    gridTemplateAreas.row2.push("icon");
    gridTemplateAreas.row3.push("icon");
    gridTemplateAreas.row4.push("icon");
    gridTemplateColumns.push(ICON_SIZE);
  }

  if (props.$hasSubtitle) {
    gridTemplateAreas.row1.push(".");
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("subtitle");
    gridTemplateAreas.row4.push(".");
    gridTemplateColumns.push("1fr");
  } else {
    gridTemplateAreas.row1.push("title");
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("title");
    gridTemplateAreas.row4.push("title");
    gridTemplateColumns.push("1fr");
  }

  gridTemplateAreas.row1.push("rhs");
  gridTemplateAreas.row2.push("rhs");
  gridTemplateAreas.row3.push("rhs");
  gridTemplateAreas.row4.push("rhs");
  gridTemplateColumns.push("auto");

  return {
    gridTemplateRows: "auto 1fr",
    gridTemplateColumns: gridTemplateColumns.join(" "),
    gridTemplateAreas: `
      '${gridTemplateAreas.row1.join(" ")}'
      '${gridTemplateAreas.row2.join(" ")}'
      '${gridTemplateAreas.row3.join(" ")}'
      '${gridTemplateAreas.row4.join(" ")}'
    `,
    columnGap: "12px",
    rowGap: "0px",
  };
};

const Container = styled.div`
  width: 100%;
`;

const SelectMenuButtonWrapper = styled.div<{ $visible: boolean; $disabled: boolean }>`
  display: inline-block;

  color: ${(props) => (props.$visible ? style.colors.blue.dark : style.colors.grey.dark)};

  :active {
    color: ${style.colors.blue.dark};
  }

  :hover {
    ${(props) => {
      if (!props.$disabled) {
        return { color: style.colors.blue.dark };
      }
    }}
  }
`;

type Placeholder = Pick<PropOption, "title" | "subtitle" | "icon">;

type Props = {
  /**
   * The name of the select menu.
   */
  name: string;

  /**
   * The value of the currently selected option.
   */
  selected?: string | null;

  onSelect: (slug: string) => void;

  /**
   * The options that appear in the select menu.
   */
  options: PropOption[];

  /**
   * A label to display above the select menu.
   * @default undefined
   */
  label?: PropLabel;

  /**
   * A custom error to display for the input. Overrides standard errors created by `required` or `pattern`.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * When the input is required.
   * @default false
   */
  required?: boolean;

  /**
   * A placeholder to display when no option is selected.
   */
  placeholder?: string | Placeholder;

  /**
   * Whether the select menu is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * An optional header to display above the list of options.
   * @default null
   */
  listHeader?: React.ReactNode;

  /**
   * An optional footer to display below the list of options.
   * @default null
   */
  listFooter?: React.ReactNode;

  /**
   * Whether the menu should display as compacted element.
   * Use this when you want the menu to be the same height as input elements.
   * @default false
   */
  compact?: boolean;

  /**
   * Testing utility function. Called with each option's `value` prop.
   * @default undefined
   */
  "data-testid"?: (value: string) => string;

  /**
   * The number of items to render above and below the visible items in the list.
   * @private
   * @default 10
   */
  UNSAFE_overscanCount?: number;

  /**
   * Dynamically calculates the height of each item in the list. Necessary when items have variable heights.
   * WARNING: This is very slow and should only be used when absolutely necessary with a short list of items.
   * Should not be used in scenarios where the list of options is long or frequently changing.
   * @private
   * @default false
   */
  UNSAFE_precalculateItemHeights?: boolean;
};

type SelectMenuRef = {
  show(): void;
  hide(): void;
  toggle(): void;
  open: boolean;
};

type SelectedOptionResult = {
  placeholder: boolean;
  option: Partial<PropOption>;
};

const getSelectedOption = (options: PropOption[], selected: string | null, placeholder?: string | Placeholder): SelectedOptionResult => {
  const option = options.find((option) => option.value === selected);

  if (option) {
    return { placeholder: false, option };
  }

  if (!placeholder) {
    return { placeholder: false, option: { title: "" } };
  }

  if (typeof placeholder === "string") {
    return { placeholder: true, option: { title: placeholder } };
  }

  return { placeholder: true, option: placeholder };
};

export const BaseSelectMenu: React.FC<Props> = (props) => {
  const {
    options,
    name,
    placeholder,
    selected = null,
    disabled = false,
    listHeader = null,
    listFooter = null,
    compact = false,
    required = false,
    UNSAFE_overscanCount = 10,
    UNSAFE_precalculateItemHeights = false,
  } = props;
  const testId = props["data-testid"];

  const errors = PropError.groupAll(props.error, null, disabled);

  const selectOptionResult = getSelectedOption(options, selected, placeholder);
  const labelledBy = `${name}-label`;

  const menuRef = React.useRef<SelectMenuRef>(null);
  const labelRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const labelIsFocused = useIsFocused(labelRef);
  const inputIsFocused = useIsFocused(inputRef);

  const onSelect = React.useCallback(
    (value: string) => {
      batch(() => {
        props.onSelect(value);

        /**
         * We need to wait until the next tick to focus the input, otherwise the
         * menu will capture the focus when the menu is closed.
         */
        queueMicrotask(() => {
          labelRef.current?.focus();
        });
      });
    },
    [props.onSelect, menuRef.current, labelRef.current]
  );

  const gridTemplate = React.useMemo(() => {
    return calculateGridTemplate({
      $hasSubtitle: selectOptionResult.option?.subtitle !== undefined,
      $hasIcon: selectOptionResult.option?.icon !== undefined,
    });
  }, [selectOptionResult.option?.subtitle !== undefined, selectOptionResult.option?.icon !== undefined]);

  return (
    <Container>
      {props.label && PropLabel.renderForBeforeInput(props.label, { required, invalid: !!errors.some })}
      <HeadlessSelectMenu
        width="button"
        ref={menuRef}
        name={name}
        options={options}
        disabled={disabled}
        selected={selected}
        onSelect={onSelect}
        data-testid={testId}
        compact={compact}
        listHeader={listHeader}
        listFooter={listFooter}
        UNSAFE_overscanCount={UNSAFE_overscanCount}
        UNSAFE_relativeShiftY={-1}
        UNSAFE_precalculateItemHeights={UNSAFE_precalculateItemHeights}
      >
        {(params) => (
          <FatButton.Container
            ref={labelRef}
            $horizontal={false}
            $highlight={false}
            $disabled={disabled}
            $invalid={false}
            $focused={labelIsFocused || inputIsFocused}
            $flatTopLeft={false}
            $flatTopRight={false}
            $flatBottomLeft={params.open}
            $flatBottomRight={params.open}
            $compact={compact}
            $hideOverflow
          >
            <FatButton.Grid $compact={compact} $gridTemplate={gridTemplate}>
              <input type="hidden" aria-labelledby={labelledBy} ref={inputRef} name={name} value={props.selected ?? undefined} />

              {selectOptionResult.option?.icon && (
                <FatButton.IconWrapper $faded={disabled || selectOptionResult.placeholder}>
                  <Icon aria-hidden icon={selectOptionResult.option.icon} xlarge />
                </FatButton.IconWrapper>
              )}

              <FatButton.TitleWrapper $faded={disabled || selectOptionResult.placeholder} $hideOverflow id={labelledBy}>
                <FatButton.TitleText>{selectOptionResult.option.title}</FatButton.TitleText>
              </FatButton.TitleWrapper>

              {selectOptionResult.option?.subtitle && (
                <FatButton.SubtitleWrapper $faded={disabled || selectOptionResult.placeholder} $hideOverflow>
                  <FatButton.SubtitleText $disabled={disabled} $highlight={false}>
                    {selectOptionResult.option?.subtitle}
                  </FatButton.SubtitleText>
                </FatButton.SubtitleWrapper>
              )}

              <FatButton.RightSideWrapper $faded={disabled}>
                <FatButton.TitleText>
                  {selectOptionResult.option?.rightSide}
                  <Box mr="12px" width="0px" />

                  <SelectMenuButtonWrapper $visible={params.open} $disabled={params.disabled}>
                    <Icon icon={params.open ? "arrow-up" : "arrow-down"} />
                  </SelectMenuButtonWrapper>

                  <Box mr="5px" width="0px" />
                </FatButton.TitleText>
              </FatButton.RightSideWrapper>
            </FatButton.Grid>
          </FatButton.Container>
        )}
      </HeadlessSelectMenu>
      {PropError.renderGroupAfterInput(errors)}
    </Container>
  );
};

BaseSelectMenu.displayName = "BaseSelectMenu";
